export type NotificationPermissionState = 'default' | 'granted' | 'denied' | 'unsupported'
export type DevicePermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported'

export function readNotificationPermission(): NotificationPermissionState {
  if (typeof Notification === 'undefined') return 'unsupported'
  return Notification.permission
}

export async function queryDevicePermission(
  name: 'geolocation' | 'camera',
): Promise<DevicePermissionState> {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return 'prompt'
  try {
    const status = await navigator.permissions.query({ name } as PermissionDescriptor)
    if (status.state === 'granted' || status.state === 'denied' || status.state === 'prompt') {
      return status.state
    }
    return 'prompt'
  } catch {
    return 'prompt'
  }
}

const needsAllow = (state?: DevicePermissionState) => state === 'prompt' || state === 'denied'

export function shouldPromptDeviceAccess(input: {
  notificationPermission: NotificationPermissionState
  locationPermission?: DevicePermissionState
  cameraPermission?: DevicePermissionState
  dismissedThisSession: boolean
}): boolean {
  if (input.dismissedThisSession) return false
  if (input.notificationPermission === 'default' || input.notificationPermission === 'denied') {
    return true
  }
  if (needsAllow(input.locationPermission) || needsAllow(input.cameraPermission)) {
    return true
  }
  return false
}

export async function requestNotificationPermission(): Promise<NotificationPermissionState> {
  if (typeof Notification === 'undefined') return 'unsupported'
  const result = await Notification.requestPermission()
  return result
}

export async function requestLocationAccess(): Promise<'granted' | 'denied' | 'unsupported'> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return 'unsupported'
  return new Promise(resolve => {
    navigator.geolocation.getCurrentPosition(
      () => resolve('granted'),
      () => resolve('denied'),
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 10_000 },
    )
  })
}

export async function requestCameraAccess(): Promise<'granted' | 'denied' | 'unsupported'> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return 'unsupported'
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
    stream.getTracks().forEach(track => track.stop())
    return 'granted'
  } catch {
    return 'denied'
  }
}
